import React from "react";
import { OUTBOUND_URL } from "../data/characters";
import { Quote, Sparkles, Heart, Flame } from "lucide-react";

const POPULAR_TOPICS = [
  "Character AI Chat",
  "AI Character Chat",
  "AI Companion",
  "Romance AI",
  "Flirty AI Chat",
  "Late-Night Talk",
  "AI Dating Sim",
  "Character AI Alternative",
  "AI Roleplay",
  "AI Character Generator",
  "Virtual Character Chat",
  "AI Sexting Alternative",
  "Cozy Chat AI",
  "After-Dark AI",
  "Creative Writing AI",
  "Anime AI Chat",
  "Fantasy AI Chat",
  "AI Girlfriend Chat",
  "AI Story Chat",
  "Private AI Chat",
];

const SeoBlock = () => {
  return (
    <section
      id="about-ai-character-chat"
      data-testid="seo-block"
      className="bg-[#FDFDFD] border-t-2 border-black py-24 sm:py-32"
      aria-labelledby="seo-heading"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
        {/* Header row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <span
              data-testid="seo-eyebrow"
              className="text-xs sm:text-sm font-bold tracking-[0.25em] uppercase font-body text-neutral-500"
            >
              The Complete Guide
            </span>
            <h2
              id="seo-heading"
              data-testid="seo-title"
              className="font-display mt-4 text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.92]"
            >
              The Anatomy of a <br />
              <span className="relative inline-block">
                <span className="relative z-10">Great Character AI Chat</span>
                <span
                  aria-hidden
                  className="absolute left-0 bottom-1 sm:bottom-2 h-3 sm:h-5 w-full bg-[#00E5FF] -z-0"
                />
              </span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="font-body text-base sm:text-lg text-neutral-700 leading-relaxed">
              Not every AI character chat is built the same. The best
              character AI chat experiences feel natural because they combine
              personality, memory, speed, privacy and roleplay depth. Here is
              what makes a conversation feel real.
            </p>
          </div>
        </div>

        {/* Magazine grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Tile 1 — dark ink card: Personality */}
          <article
            data-testid="seo-tile-1"
            className="md:col-span-5 bg-[#0A0A0A] text-white p-8 sm:p-10 rounded-2xl relative overflow-hidden"
          >
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-[#00E5FF]">
              <Sparkles className="w-4 h-4" /> 01 · Personality
            </div>
            <h3 className="font-display mt-6 text-3xl sm:text-4xl font-black tracking-tight uppercase leading-[0.95]">
              AI Characters With Real Voice
            </h3>
            <p className="font-body mt-5 text-white/80 leading-relaxed">
              A shallow prompt makes every AI character sound the same. A
              strong character AI chat needs a clear personality, emotional
              range, speech patterns, boundaries, interests and a backstory
              that actually shows up in conversation.
            </p>
            <p className="font-body mt-4 text-white/80 leading-relaxed">
              A good AI character does not just answer. It reacts, remembers,
              teases, asks questions and keeps the mood consistent.
            </p>
            <div className="mt-6 inline-flex items-center gap-3 bg-white/5 border border-white/15 px-4 py-2">
              <Flame className="w-4 h-4 text-[#FF9500]" />
              <span className="text-sm font-bold tracking-wide uppercase">
                Distinct Archetypes
              </span>
            </div>
          </article>

          {/* Tile 2 — stat block cyan: Avg reply */}
          <div
            data-testid="seo-tile-2"
            className="md:col-span-3 bg-[#00E5FF] text-black p-8 sm:p-10 rounded-2xl flex flex-col justify-between min-h-[280px]"
          >
            <span className="text-xs font-bold tracking-[0.25em] uppercase">
              Avg. Reply
            </span>
            <div>
              <div className="font-display text-6xl sm:text-7xl font-black tracking-tighter leading-none">
                0.4<span className="text-[#FF2D55]">s</span>
              </div>
              <p className="font-body mt-3 text-sm text-black/80 leading-relaxed">
                Every character AI chat should move at the speed of a real
                conversation. Fast replies keep the roleplay alive and make
                the interaction feel natural.
              </p>
            </div>
          </div>

          {/* Tile 3 — yellow pull quote */}
          <blockquote
            data-testid="seo-tile-3"
            className="md:col-span-4 bg-[#FFD60A] text-black p-8 sm:p-10 rounded-2xl flex flex-col justify-between min-h-[280px]"
          >
            <Quote className="w-10 h-10 -ml-1" strokeWidth={2.5} />
            <div>
              <p className="font-display text-xl sm:text-2xl font-black tracking-tight leading-snug uppercase">
                &ldquo;It is the first AI character chat that actually
                remembers what I told it three hours ago.&rdquo;
              </p>
              <footer className="mt-5 text-sm font-bold tracking-[0.18em] uppercase">
                — Beta user
              </footer>
            </div>
          </blockquote>

          {/* Tile 4 — wide white panel: Memory */}
          <article
            data-testid="seo-tile-4"
            className="md:col-span-7 bg-white border-2 border-black p-8 sm:p-10 rounded-2xl"
          >
            <div className="flex items-center gap-2 text-xs font-bold tracking-[0.25em] uppercase text-neutral-500">
              <Heart className="w-4 h-4 text-[#FF2D55]" /> 02 · Memory
            </div>
            <h3 className="font-display mt-5 text-3xl sm:text-4xl font-black tracking-tight uppercase leading-[0.95]">
              Conversations That Carry Over
            </h3>
            <p className="font-body mt-5 text-neutral-700 leading-relaxed">
              A great <strong>AI character</strong> remembers your name, the
              side plot you started, the relationship dynamic, the emotional
              tone and the details that matter. Your chat should not feel
              like a new conversation every time you return.
            </p>

            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <li className="flex gap-3">
                <span className="w-1.5 h-6 bg-[#FF2D55] shrink-0" />
                <span className="font-display font-black uppercase text-base">
                  Long-term recall
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-1.5 h-6 bg-[#0055FF] shrink-0" />
                <span className="font-display font-black uppercase text-base">
                  Tone adaptation
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-1.5 h-6 bg-[#14B8A6] shrink-0" />
                <span className="font-display font-black uppercase text-base">
                  Private by default
                </span>
              </li>
              <li className="flex gap-3">
                <span className="w-1.5 h-6 bg-[#FF9500] shrink-0" />
                <span className="font-display font-black uppercase text-base">
                  Cross-device sync
                </span>
              </li>
            </ul>
          </article>

          {/* Tile 5 — pink Roleplay tile */}
          <article
            data-testid="seo-tile-5"
            className="md:col-span-5 bg-[#FF2D55] text-white p-8 sm:p-10 rounded-2xl flex flex-col justify-between min-h-[320px]"
          >
            <div>
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-white/80">
                03 · Roleplay
              </div>
              <h3 className="font-display mt-5 text-3xl sm:text-4xl font-black tracking-tight uppercase leading-[0.95]">
                Built for Immersive Stories, Not Q&amp;A
              </h3>
              <p className="font-body mt-5 text-white/95 leading-relaxed">
                Character AI chat is not only about asking questions. It is
                about building scenes, relationships and stories. Slow-burn
                romance, fantasy adventures, comedy, mystery, anime roleplay
                or emotional drama — the character should stay inside the
                world you create.
              </p>
            </div>

            <a
              data-testid="seo-tile-cta"
              href={OUTBOUND_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 self-start inline-flex items-center gap-2 bg-black text-white font-display font-black uppercase tracking-tight text-base px-6 py-3.5 border-2 border-black brutal-shadow-light"
            >
              Pick a Character →
            </a>
          </article>

          {/* Tile 6 — popular topics: each chip is an outbound link */}
          <div
            data-testid="seo-tile-6"
            className="md:col-span-7 border-2 border-black p-8 sm:p-10 rounded-2xl bg-neutral-50"
          >
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-neutral-500">
              Popular Topics
            </span>
            <h3 className="font-display mt-4 text-2xl sm:text-3xl font-black tracking-tight uppercase">
              What People Chat About With AI Characters
            </h3>
            <ul
              data-testid="popular-topics-list"
              className="mt-6 flex flex-wrap gap-2"
            >
              {POPULAR_TOPICS.map((t, i) => (
                <li key={t}>
                  <a
                    data-testid={`popular-topic-${i}`}
                    href={OUTBOUND_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Start a chat about ${t}`}
                    className="inline-block text-sm font-bold tracking-wide uppercase border-2 border-black px-3 py-1.5 bg-white hover:bg-[#00E5FF] hover:text-black transition cursor-pointer"
                  >
                    {t}
                  </a>
                </li>
              ))}
            </ul>

            <p className="font-body mt-8 text-neutral-700 leading-relaxed max-w-3xl">
              Whether you want a sweet slow-burn romance, an anime roleplay
              partner, a fantasy companion, a dramatic villain, a private AI
              girlfriend chat or a creative writing partner, character AI
              chat lets you switch between personalities without losing your
              rhythm.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SeoBlock;
